import { FC, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import { EGiftCardStatus, TGiftCard, TTransaction } from '@xeppt/xeppt-sdk/types';
import { formatDate, getGiftCardStatusVariant, prettifyAmount } from '@utils/index';
import { formatFeeRate, getFeesPlanSendAndRequest } from '@utils/payment';
import { EFundsOperation } from '@enum';
import { useLocales } from '@hooks/helpers/useLocales';
import { getGiftCardStatusesList } from '@utils/billing';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import Typography from '@components/common/typography';
import { currencies } from '@const/currency';
import Tabs from '@components/tabs';
import InfoBlock from '@components/common/info_block';
import Textarea from '@components/common/textarea';
import Input from '@components/common/input';
import Radio from '@components/common/radio';
import Phone from '@components/phone';
import { isValidEmail } from '@helpers/validation_objects';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiGiftCardsService, apiTransactionsService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import ViewTransactionModal from '../../global/view_transaction';
import scanQrIllustration from '@svg/illustrations/scan_qr.svg';

type TGiftCardTab = 'details' | 'history';

const ViewGiftCardModal: FC<
    IModalComponentProps<{ giftCard: TGiftCard; refetchGiftCards?: () => void }>
> = ({ onClose, modalData, ...restProps }) => {
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const { account, loyalty } = useUserContext();
    const [activeTab, setActiveTab] = useState<TGiftCardTab>('details');
    const [showFees, setShowFees] = useState(false);
    const [showActivate, setShowActivate] = useState(false);
    const [chosenRedemptionTxnId, setChosenRedemptionTxnId] = useState('');
    const [chosenTransactionData, setChosenTransactionData] = useState<TTransaction | null>(null);
    // In-drawer edit sub-views (push, like the Total fees / Send for activation views).
    // liveGiftCard holds a freshly-refetched card after an edit so the detail reflects
    // the change without closing the drawer.
    const [liveGiftCard, setLiveGiftCard] = useState<TGiftCard | null>(null);
    const [showDescriptionEdit, setShowDescriptionEdit] = useState(false);
    const [descriptionValue, setDescriptionValue] = useState('');
    const [showRecipientEdit, setShowRecipientEdit] = useState(false);
    const [recipientNotify, setRecipientNotify] = useState<'email' | 'phone'>('email');
    const [recipientData, setRecipientData] = useState({
        email: '',
        phone_code: '1',
        phone_number: ''
    });
    const { handleCancelGiftCards, handleRequestCancelGiftCards } = useGiftCardsModals(modalData);

    const { handleRequest: handleSendGiftCard, isLoading: isSendLoading } = useApiMutation({
        method: (id: string) => apiGiftCardsService.sendGiftCard(id),
        onSuccess: () => {
            requestSuccessLocale('gift_card_sent_successfully');
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleActivate, isLoading: isActivateLoading } = useApiMutation({
        method: () =>
            apiGiftCardsService.activateGiftCard([modalData?.giftCard?.id ?? '']),
        onSuccess: () => {
            requestSuccessLocale('gift_card_activated');
            modalData?.refetchGiftCards?.();
            onClose();
        },
        onError: requestErrorLocale
    });

    // Refetch the single card after an in-drawer edit so the detail reflects the change
    // without closing the drawer (kept open, like backing out of the Total fees view).
    const { handleRequest: refetchGiftCardDetail } = useApiMutation({
        method: (id: string) => apiGiftCardsService.getGiftCardById(id),
        onSuccess: (data) => setLiveGiftCard(data),
        onError: () => {}
    });

    const { handleRequest: handleSaveDescription, isLoading: isDescriptionSaving } = useApiMutation({
        method: () =>
            apiGiftCardsService.updateGiftCard(modalData?.giftCard?.id ?? '', {
                description: descriptionValue
            }),
        onSuccess: () => {
            requestSuccessLocale('gift_card_updated');
            modalData?.refetchGiftCards?.();
            refetchGiftCardDetail(modalData?.giftCard?.id ?? '');
            setShowDescriptionEdit(false);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleSaveRecipient, isLoading: isRecipientSaving } = useApiMutation({
        // Send ONLY the chosen channel; the BE makes the recipient exclusive (setting
        // email clears phone and vice versa). We must NOT send empty strings for the other
        // channel — the BE phone validators reject "" (omitempty doesn't skip a present
        // empty string), which 400s. Bugs 11.0 #1: switching to "phone" used to leave the
        // old email set and the detail preferred (showed) email.
        method: () =>
            apiGiftCardsService.updateGiftCard(
                modalData?.giftCard?.id ?? '',
                recipientNotify === 'email'
                    ? { email: recipientData.email }
                    : {
                          phone_code: recipientData.phone_code,
                          phone_number: recipientData.phone_number
                      }
            ),
        onSuccess: () => {
            requestSuccessLocale('gift_card_updated');
            modalData?.refetchGiftCards?.();
            refetchGiftCardDetail(modalData?.giftCard?.id ?? '');
            setShowRecipientEdit(false);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: fetchRedemptionTransaction } = useApiQuery({
        method: () => apiTransactionsService.getTransactionById(chosenRedemptionTxnId || ''),
        isInitialRequest: false,
        condition: !!chosenRedemptionTxnId,
        onSuccess: (data) => {
            setChosenTransactionData(data);
            setChosenRedemptionTxnId('');
        },
        onError: () => {
            setChosenRedemptionTxnId('');
        }
    });

    useEffect(() => {
        if (chosenRedemptionTxnId) {
            fetchRedemptionTransaction();
        }
    }, [chosenRedemptionTxnId]);

    if (!modalData?.giftCard) return null;
    const giftCard = liveGiftCard ?? modalData.giftCard;

    const openDescriptionEdit = () => {
        setDescriptionValue(giftCard.description || '');
        setShowDescriptionEdit(true);
    };

    const openRecipientEdit = () => {
        setRecipientData({
            email: giftCard.email || '',
            phone_code: giftCard.phone_code || '1',
            phone_number: giftCard.phone_number || ''
        });
        setRecipientNotify(giftCard.email ? 'email' : giftCard.phone_number ? 'phone' : 'email');
        setShowRecipientEdit(true);
    };

    const isRecipientValid =
        recipientNotify === 'email'
            ? isValidEmail(recipientData.email)
            : !!recipientData.phone_code && !!recipientData.phone_number;

    const currency = giftCard.currency ?? 'CAD';
    const symbol = currencies[currency as 'CAD']?.symbol ?? '$';
    // Issuing fee (charged at card creation) and redemption fees (charged per redemption)
    // are both exposed by the BE: `fee` on the card, and `fee` on each redemption-history
    // row. SDK types may lag, so both are read via guarded casts.
    const issuingFee = (giftCard as any)?.fee as number | undefined;
    const hasIssuingFee = issuingFee !== undefined && issuingFee > 0;

    const redemptionHistory = giftCard.redemption_history ?? [];
    // Sum of redemption fees actually charged on this card (clamped >=0; fees are charges).
    const redemptionFee = Math.max(
        0,
        redemptionHistory.reduce((sum, item) => sum + (((item as any).fee as number) || 0), 0)
    );
    const hasRedemptionFee = redemptionFee > 0;
    // The redemption fee is ONE combined rate = percent (%) + flat ($), configured per tier
    // in admin → Fees (key 8_2_14) — the same model as every other XEPPT transaction fee.
    // Render the configured rate via the shared formatFeeRate helper (e.g. "1% + $0.50 CAD"),
    // not a single derived percentage.
    const redemptionFeePlan = getFeesPlanSendAndRequest({
        loyalty,
        method: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM
    });
    const redemptionRate = formatFeeRate({
        percent: redemptionFeePlan?.percent,
        flat: redemptionFeePlan?.flat,
        currency
    });
    const totalFees = (issuingFee || 0) + redemptionFee;
    const hasAnyFee = hasIssuingFee || hasRedemptionFee;

    const hasRecipient =
        !!giftCard.email || (!!giftCard.phone_code && !!giftCard.phone_number);

    const isInsufficient = !!account && giftCard.amount > (account.balance ?? 0);

    const recipientLabel = hasRecipient
        ? giftCard.email
            ? giftCard.email
            : `${giftCard.phone_code?.includes('+') ? giftCard.phone_code : `+${giftCard.phone_code}`} ${giftCard.phone_number}`
        : '-';

    const showActionBar =
        giftCard.status === EGiftCardStatus.ISSUED ||
        giftCard.status === EGiftCardStatus.PENDING ||
        giftCard.status === EGiftCardStatus.ACTIVE;

    const handleCancelClick = () => {
        if (giftCard.status === EGiftCardStatus.ISSUED) {
            handleCancelGiftCards([giftCard]);
        } else {
            handleRequestCancelGiftCards([giftCard]);
        }
    };

    const tabList = [
        { label: 'Details', value: 'details' },
        { label: 'Redemption history', value: 'history' }
    ];

    // ── In-drawer: chosen redemption transaction detail ────────────────────────
    if (chosenTransactionData !== null) {
        return (
            <ViewTransactionModal
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(restProps as any)}
                modalData={{
                    transaction: chosenTransactionData,
                    onBack: () => setChosenTransactionData(null)
                }}
                onClose={onClose}
            />
        );
    }

    // ── In-drawer: "Send for activation" confirmation ──────────────────────────
    if (showActivate) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.gc_detail_header}>
                    <button
                        type="button"
                        className={styles.gc_back_btn}
                        onClick={() => setShowActivate(false)}>
                        <Icon name="arrow_left" width={24} height={24} />
                    </button>
                    <Typography fz={24} fw={700} alignment="center">
                        Send for activation
                    </Typography>
                    <button
                        type="button"
                        className={styles.gc_close_btn}
                        onClick={onClose}>
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.activation_content}>
                    <div className={styles.activation_balance}>
                        <Typography variant="body3">
                            Current XEPPT wallet balance:{' '}
                            <b>
                                {symbol}
                                {prettifyAmount((account?.balance ?? 0).toFixed(2))}
                            </b>
                        </Typography>
                    </div>
                    {isInsufficient && (
                        <div className={styles.activation_insufficient}>
                            <Icon name="filled_warning" />
                            <Typography variant="body4">Insufficient balance</Typography>
                        </div>
                    )}
                    <div className={styles.activation_summary}>
                        <div className={styles.activation_block}>
                            <Typography variant="body3" fw={600} className={styles.activation_block_title}>
                                Gift card details
                            </Typography>
                            <div className={styles.activation_rows}>
                                <div className={styles.activation_row}>
                                    <Typography variant="body3">Gift card number</Typography>
                                    <Typography variant="body4">
                                        {giftCard.gift_card_serial_number}
                                    </Typography>
                                </div>
                                <div className={styles.activation_row}>
                                    <Typography variant="body4">Gift card value</Typography>
                                    <div>
                                        {symbol}
                                        {prettifyAmount(giftCard.amount.toFixed(2))} {currency}
                                    </div>
                                </div>
                                <div className={styles.activation_row}>
                                    <Typography variant="body4">
                                        Amount that will be reserved
                                    </Typography>
                                    <span className={styles.activation_bold}>
                                        {symbol}
                                        {prettifyAmount(giftCard.amount.toFixed(2))} {currency}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <hr className={styles.activation_divider} />
                        <div className={styles.activation_block}>
                            <Typography variant="body3" fw={600} className={styles.activation_block_title}>
                                Recipients details
                            </Typography>
                            <div className={styles.activation_rows}>
                                <div className={styles.activation_row}>
                                    <Typography variant="body4">Recipient</Typography>
                                    <span>{recipientLabel}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            size="medium"
                            disabled={isInsufficient}
                            isLoading={isActivateLoading}
                            onClick={handleActivate}>
                            Confirm activation
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── In-drawer: add/edit description ────────────────────────────────────────
    if (showDescriptionEdit) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.gc_detail_header}>
                    <button
                        type="button"
                        className={styles.gc_back_btn}
                        onClick={() => setShowDescriptionEdit(false)}>
                        <Icon name="arrow_left" width={24} height={24} />
                    </button>
                    <Typography fz={24} fw={700} alignment="center">
                        {giftCard.description ? 'Edit description' : 'Add description'}
                    </Typography>
                    <button type="button" className={styles.gc_close_btn} onClick={onClose}>
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.edit_content}>
                    <Textarea
                        label="Description"
                        full
                        value={descriptionValue}
                        onChange={(val) => setDescriptionValue(val)}
                        placeholder="Add a description for the recipient"
                        helperText="This description will be shown to the recipient with the gift card"
                    />
                </div>
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            size="medium"
                            disabled={!descriptionValue}
                            isLoading={isDescriptionSaving}
                            onClick={handleSaveDescription}>
                            Save changes
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── In-drawer: add/edit recipient details ──────────────────────────────────
    if (showRecipientEdit) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.gc_detail_header}>
                    <button
                        type="button"
                        className={styles.gc_back_btn}
                        onClick={() => setShowRecipientEdit(false)}>
                        <Icon name="arrow_left" width={24} height={24} />
                    </button>
                    <Typography fz={24} fw={700} alignment="center">
                        {hasRecipient ? 'Edit recipient details' : 'Add recipient details'}
                    </Typography>
                    <button type="button" className={styles.gc_close_btn} onClick={onClose}>
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.edit_content}>
                    <div className={styles.recipient_radios}>
                        <div
                            className={styles.recipient_radio}
                            onClick={() => setRecipientNotify('email')}>
                            <Radio checked={recipientNotify === 'email'} />
                            <Typography>Email address</Typography>
                        </div>
                        <div
                            className={styles.recipient_radio}
                            onClick={() => setRecipientNotify('phone')}>
                            <Radio checked={recipientNotify === 'phone'} />
                            <Typography>Phone number</Typography>
                        </div>
                    </div>
                    {recipientNotify === 'email' ? (
                        <Input
                            full
                            label="Recipient email"
                            placeholder="Enter email address"
                            value={recipientData.email}
                            onChange={(value) =>
                                setRecipientData((state) => ({ ...state, email: value }))
                            }
                        />
                    ) : (
                        <Phone
                            label="Recipient phone number"
                            isForm={false}
                            phone_code={recipientData.phone_code}
                            phone_number={recipientData.phone_number}
                            onCodeChange={(val) =>
                                setRecipientData((state) => ({ ...state, phone_code: val }))
                            }
                            onPhoneChange={(val) =>
                                setRecipientData((state) => ({ ...state, phone_number: val }))
                            }
                        />
                    )}
                </div>
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            size="medium"
                            disabled={!isRecipientValid}
                            isLoading={isRecipientSaving}
                            onClick={handleSaveRecipient}>
                            {hasRecipient ? 'Save changes' : 'Add recipient'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ── In-drawer: total fees sub-view ─────────────────────────────────────────
    if (showFees) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.gc_detail_header}>
                    <button
                        type="button"
                        className={styles.gc_back_btn}
                        onClick={() => setShowFees(false)}>
                        <Icon name="arrow_left" width={20} height={20} />
                    </button>
                    <Typography fz={24} fw={700} alignment="center">
                        Total fees
                    </Typography>
                    <button
                        type="button"
                        className={styles.gc_close_btn}
                        onClick={onClose}>
                        <Icon name="close" width={20} height={20} />
                    </button>
                </div>
                <div className={styles.fees_rows}>
                    {hasIssuingFee && (
                        <div className={styles.fees_item}>
                            <p>Gift card issuing fee</p>
                            <div className={styles.fees_item_right}>
                                <p>{`${symbol}${prettifyAmount((issuingFee as number).toFixed(2))} ${currency}`}</p>
                                <p className={styles.fees_item_note}>Paid upon card creation</p>
                            </div>
                        </div>
                    )}
                    {hasRedemptionFee && (
                        <div className={styles.fees_item}>
                            <p>{`Gift card redemption fee${redemptionRate ? ` (${redemptionRate})` : ''}`}</p>
                            <div className={styles.fees_item_right}>
                                <p>{`${symbol}${prettifyAmount(redemptionFee.toFixed(2))} ${currency}`}</p>
                                <p className={styles.fees_item_note}>Included in redeemed amount</p>
                            </div>
                        </div>
                    )}
                    <hr className={styles.fees_divider} />
                    <div className={styles.fees_item}>
                        <p className={styles.fees_total}>
                            <b>Total</b>
                        </p>
                        <p className={styles.fees_total}>
                            <b>
                                {hasAnyFee
                                    ? `${symbol}${prettifyAmount(totalFees.toFixed(2))} ${currency}`
                                    : '-'}
                            </b>
                        </p>
                    </div>
                </div>
                <InfoBlock variant="gray">
                    {hasIssuingFee &&
                        'The gift card issuing fee is charged separately from the gift card value at the time of card creation. '}
                    {hasRedemptionFee &&
                        'The gift card redemption fee is included in the redeemed amount.'}
                </InfoBlock>
            </div>
        );
    }

    // ── Main gift card detail view ─────────────────────────────────────────────
    return (
        <div className={styles.wrapper}>
            <div className={styles.gc_detail_header}>
                <Typography fz={24} fw={700} alignment="center">
                    Gift card details
                </Typography>
                <button
                    type="button"
                    className={styles.gc_close_btn}
                    onClick={onClose}>
                    <Icon name="close" width={20} height={20} />
                </button>
            </div>
            <div className={styles.content}>
                <div className={styles.hero_card}>
                    <div className={styles.hero_icon}>
                        <Icon name="gift_card" width={28} height={28} />
                    </div>
                    <p className={styles.hero_serial}>{giftCard.gift_card_serial_number}</p>
                    <Status variant={getGiftCardStatusVariant(giftCard.status)}>
                        {
                            getGiftCardStatusesList.find(
                                (item) => item.value === giftCard.status
                            )?.label
                        }
                    </Status>
                    <p className={styles.hero_balance_label}>Gift card balance</p>
                    <p className={styles.hero_amount}>
                        {symbol}
                        {prettifyAmount((giftCard.available_amount ?? 0).toFixed(2))} {currency}
                    </p>
                </div>
                <div className={styles.tabs_section}>
                    <Tabs
                        onChange={(val) => setActiveTab(val as TGiftCardTab)}
                        tabs={tabList}
                        activeTab={activeTab}
                    />
                    {activeTab === 'details' && (
                        <div className={styles.details}>
                            <div className={styles.row}>
                                <p>Original amount</p>
                                <p>
                                    {symbol}
                                    {prettifyAmount((giftCard.amount ?? 0).toFixed(2))} {currency}
                                </p>
                            </div>
                            {hasAnyFee && (
                                <div className={styles.row}>
                                    <p>
                                        {hasIssuingFee ? 'Issuing fees' : 'Redemption fees'}
                                        <button
                                            type="button"
                                            className={styles.fee_info_btn}
                                            onClick={() => setShowFees(true)}>
                                            <Icon name="info" width={16} height={16} />
                                        </button>
                                    </p>
                                    <p>{`${symbol}${prettifyAmount((hasIssuingFee ? (issuingFee as number) : redemptionFee).toFixed(2))} ${currency}`}</p>
                                </div>
                            )}
                            <div className={styles.row}>
                                <p>Issued</p>
                                <p>{formatDate(giftCard.created_at, 'DD MMMM YYYY')}</p>
                            </div>
                            {(giftCard.points ?? 0) > 0 && (
                                <div className={styles.row}>
                                    <p>Points you earned</p>
                                    <p>{giftCard.points}</p>
                                </div>
                            )}
                            <div className={styles.row}>
                                <p>Description</p>
                                <p className={styles.row_value}>
                                    {giftCard.description ? (
                                        <span className={styles.with_action}>
                                            {giftCard.description}
                                            {giftCard.status === EGiftCardStatus.ISSUED && (
                                                <Button
                                                    leftIcon="edit"
                                                    variant="text-gold"
                                                    size="small"
                                                    onClick={openDescriptionEdit}
                                                />
                                            )}
                                        </span>
                                    ) : giftCard.status === EGiftCardStatus.ISSUED ? (
                                        <Button
                                            variant="text-gold"
                                            leftIcon="plus"
                                            size="small"
                                            onClick={openDescriptionEdit}>
                                            Add description
                                        </Button>
                                    ) : (
                                        '-'
                                    )}
                                </p>
                            </div>
                            {hasRecipient ? (
                                <div className={styles.row}>
                                    <p>Recipient</p>
                                    <p className={styles.row_value}>
                                        <span className={styles.with_action}>
                                            {giftCard.email
                                                ? giftCard.email
                                                : `${
                                                      giftCard.phone_code?.includes('+')
                                                          ? giftCard.phone_code
                                                          : `+${giftCard.phone_code}`
                                                  } ${giftCard.phone_number}`}
                                            {giftCard.status === EGiftCardStatus.ISSUED && (
                                                <Button
                                                    leftIcon="edit"
                                                    variant="text-gold"
                                                    size="small"
                                                    onClick={openRecipientEdit}
                                                />
                                            )}
                                        </span>
                                    </p>
                                </div>
                            ) : (
                                giftCard.status === EGiftCardStatus.ISSUED && (
                                    <div className={styles.row}>
                                        <p>Recipient</p>
                                        <p className={styles.row_value}>
                                            <Button
                                                variant="text-gold"
                                                leftIcon="plus"
                                                size="small"
                                                onClick={openRecipientEdit}>
                                                Add recipient
                                            </Button>
                                        </p>
                                    </div>
                                )
                            )}
                            {giftCard.notification_sent && giftCard.notification_sent_at && (
                                <div className={styles.row}>
                                    <p>Sent</p>
                                    <p>
                                        {formatDate(
                                            giftCard.notification_sent_at,
                                            'DD MMMM YYYY, HH:mm'
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'history' && (
                        <div className={styles.redemption_list}>
                            {(giftCard.redemption_history?.length ?? 0) > 0 ? (
                                giftCard.redemption_history?.map((item, i) => {
                                    // The BE exposes the underlying redemption transaction id as
                                    // `transaction_id` (get_gift_card_detail.go). Clicking a row opens
                                    // that transaction in-drawer via getTransactionById (caller-scoped).
                                    const txnId = (item as any).transaction_id as string | undefined;
                                    return (
                                        <div
                                            key={i}
                                            className={`${styles.redemption_item}${txnId ? ` ${styles.redemption_item_clickable}` : ''}`}
                                            onClick={
                                                txnId
                                                    ? () => setChosenRedemptionTxnId(txnId)
                                                    : undefined
                                            }>
                                            <div className={styles.redemption_left}>
                                                <Typography variant="body3">
                                                    {item.description}
                                                </Typography>
                                                <Typography
                                                    variant="body4"
                                                    className={styles.redemption_date}>
                                                    {formatDate(
                                                        item.created_at,
                                                        'DD MMMM YYYY, HH:mm'
                                                    )}
                                                </Typography>
                                            </div>
                                            <div className={styles.redemption_right}>
                                                <Typography variant="body1" weight="bold">
                                                    -{symbol}
                                                    {prettifyAmount(item.amount.toFixed(2))}
                                                </Typography>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className={styles.redemption_empty}>
                                    <img
                                        src={scanQrIllustration}
                                        alt=""
                                        className={styles.redemption_empty_illustration}
                                    />
                                    <Typography variant="body1" weight="bold" alignment="center">
                                        No redemptions yet
                                    </Typography>
                                    <Typography
                                        variant="body4"
                                        alignment="center"
                                        className={styles.redemption_empty_subtitle}>
                                        Redemption activity will appear here
                                    </Typography>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showActionBar && (
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        <Button
                            variant="outlined-red"
                            size="medium"
                            onClick={handleCancelClick}>
                            Cancel gift card
                        </Button>
                        {giftCard.status === EGiftCardStatus.ISSUED && (
                            // When no recipient is set, the button is disabled and a tooltip
                            // explains what the merchant must do first (Figma: "no recipient" state).
                            <div className={styles.send_btn_wrapper}>
                                <Button
                                    variant="primary"
                                    size="medium"
                                    disabled={!hasRecipient}
                                    onClick={() => setShowActivate(true)}>
                                    Send for activation
                                </Button>
                                {!hasRecipient && (
                                    <div className={styles.no_recipient_tooltip}>
                                        No recipient email or phone number has been added
                                    </div>
                                )}
                            </div>
                        )}
                        {(giftCard.status === EGiftCardStatus.PENDING ||
                            giftCard.status === EGiftCardStatus.ACTIVE) && (
                            <Button
                                variant="primary"
                                size="medium"
                                isLoading={isSendLoading}
                                onClick={() => handleSendGiftCard(giftCard.id)}>
                                Resend gift card
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewGiftCardModal;
